import React, { Suspense, lazy, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/layout/Layout';
import {
  RequireAuth,
  RequireApprovedForChurchApp,
} from './components/auth/RouteGuards';
import { getCurrentUser } from './store/slices/authSlice';
import { syncPushSubscriptionWithUser } from './services/pushNotificationService';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const PendingApproval = lazy(() => import('./pages/PendingApproval'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const EventsList = lazy(() => import('./pages/EventsList'));
const EventForm = lazy(() => import('./pages/EventForm'));
const EventDetails = lazy(() => import('./pages/EventDetails'));
const EventSetlistSongView = lazy(() => import('./pages/EventSetlistSongView'));
const SongList = lazy(() => import('./pages/SongList'));
const SongForm = lazy(() => import('./pages/SongForm'));
const SongDetails = lazy(() => import('./pages/SongDetails'));
const EventChat = lazy(() => import('./pages/EventChat'));
const EventTeamBuild = lazy(() => import('./pages/EventTeamBuild'));
const ProductionPlanning = lazy(() => import('./pages/ProductionPlanning'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const TeamList = lazy(() => import('./pages/TeamList'));
const TeamDetails = lazy(() => import('./pages/TeamDetails'));
const TeamChat = lazy(() => import('./pages/TeamChat'));
const TeamForm = lazy(() => import('./pages/TeamForm'));
const AutoEventScheduler = lazy(() => import('./pages/AutoEventScheduler'));
const LiveOperator = lazy(() => import('./pages/LiveOperator'));
const LiveViewer = lazy(() => import('./pages/LiveViewer'));

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (localStorage.getItem('accessToken') || localStorage.getItem('refreshToken')) {
      dispatch(getCurrentUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (user?.id) {
      syncPushSubscriptionWithUser(user.id);
    }
  }, [user?.id]);

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/live/viewer" element={<LiveViewer />} />
          <Route path="/live/viewer/:token" element={<LiveViewer />} />
          <Route path="/live/viewer/event/:eventId" element={<LiveViewer />} />
          <Route path="/live/operator/:eventId" element={<LiveOperator />} />

          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route element={<RequireAuth />}>
              <Route path="pending-approval" element={<PendingApproval />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route element={<RequireApprovedForChurchApp />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="events" element={<EventsList />} />
                <Route path="events/new" element={<EventForm />} />
                <Route path="events/:id/team" element={<EventTeamBuild />} />
                <Route path="events/:id/live" element={<LiveOperator />} />
                <Route
                  path="events/:id/setlist/:songId"
                  element={<EventSetlistSongView />}
                />
                <Route path="events/:id" element={<EventDetails />} />
                <Route path="events/:id/chat" element={<EventChat />} />
                <Route
                  path="events/:id/production"
                  element={<ProductionPlanning />}
                />
                <Route path="events/:id/edit" element={<EventForm />} />
                <Route path="teams" element={<TeamList />} />
                <Route path="teams/new" element={<TeamForm />} />
                <Route path="teams/:id" element={<TeamDetails />} />
                <Route path="teams/:id/chat" element={<TeamChat />} />
                <Route path="teams/:id/edit" element={<TeamForm />} />
                <Route path="songs" element={<SongList />} />
                <Route path="songs/new" element={<SongForm />} />
                <Route path="songs/:id" element={<SongDetails />} />
                <Route path="songs/:id/edit" element={<SongForm />} />
                <Route path="auto-scheduler" element={<AutoEventScheduler />} />
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
